import React, { useEffect, useState, useContext } from "react";
import { CombinedContext } from "../../../context/combinedContext";
import translations from "../../../translations/translations";
import companyBasedRequest from "../../../request/companyBasedRequest";
import "../adminModals.css";
import "./attributesModal.css";
import { Check } from "lucide-react";
import useModalDismiss from "../../common/useModalDismiss";

const AttributesModal = ({ open, onClose, shopId, shopData, setShopData }) => {
	useModalDismiss(onClose, { enabled: open });
	const { language, userData } = useContext(CombinedContext);

	const [allAttributes, setAllAttributes] = useState([]);
	const [selected, setSelected] = useState([]);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!open) return;

		(async () => {
			try {
				const resp = await companyBasedRequest.getAllAttributes();
				setAllAttributes(resp.attributes || []);

				// Pre-fill selected
				const activeIds = shopData.attributes?.map((a) => a.attributeId) || [];
				setSelected(activeIds);
			} catch (err) {
				console.error("Failed to load attributes:", err);
				setAllAttributes([]);
			}
		})();
	}, [open]);

	// Select toggler
	const toggleSelect = (id) => {
		setSelected((prev) =>
			prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
		);
	};

	// Save handler
	const handleSave = async () => {
		try {
			setSaving(true)
			const result = await companyBasedRequest.updateShopAttributes(shopId, selected, userData.cognitoTokens.AccessToken);

			setShopData((prev) => ({
				...prev,
				attributes: result.shop.attributes || []
			}));
		} catch (err) {
			console.error("Failed to save attributes:", err);
		} finally {
            setSaving(false);
        }

		onClose();
	};

	if (!open) return null;

	const t = translations[language].profile;
	const attrT = t.modal.attributes;

	// Normal UI
	return (
		<div className="sf-pm-overlay">
			<div className="sf-pm-modal">
				{/* Header */}
				<div className="sf-pm-header">
					<div className="sf-pm-heading">
						<h2 className="sf-pm-title">{attrT.title}</h2>
						<p className="sf-pm-sub">{attrT.description}</p>
					</div>
					<button className="sf-pm-close" onClick={onClose} aria-label={t.actions.cancel}>
						✕
					</button>
				</div>

				{/* Grid */}
				<div className="sf-pm-body">
					{allAttributes.length === 0 ? (
						<p className="sf-pm-empty">{attrT.empty}</p>
					) : (
						<section className="sf-pm-section">
							<div className="sf-pm-label-row">
								<p className="sf-pm-label">{attrT.title}</p>
								{selected.length > 0 && (
									<span className="sf-pm-count">
										{selected.length} {attrT.selected}
									</span>
								)}
							</div>

							<div className="sf-pm-attr-grid">
								{allAttributes.map((attr) => {
									const isSelected = selected.includes(attr.attributeId);

									return (
										<button
											key={attr.attributeId}
											type="button"
											aria-pressed={isSelected}
											className={`sf-pm-attr ${isSelected ? "is-selected" : ""}`}
											onClick={() => toggleSelect(attr.attributeId)}
										>
											<span className="sf-pm-attr-check" aria-hidden="true">
												<Check size={13} strokeWidth={3} />
											</span>

											<span className="sf-pm-attr-icon">
												{attr.icon ? <img src={attr.icon} alt="" /> : null}
											</span>

											<span className="sf-pm-attr-label">
												{language === "es" ? attr.nameES : attr.nameEN}
											</span>
										</button>
									);
								})}
							</div>
						</section>
					)}
				</div>

				{/* Footer */}
				<div className="sf-pm-footer">
					<button className="sf-pm-btn is-ghost" onClick={onClose} disabled={saving}>
						{translations[language].calendar.editor.cancel}
					</button>
					<button className="sf-pm-btn is-primary" onClick={handleSave} disabled={saving}>
						{saving && <span className="sf-pm-spinner" aria-hidden="true" />}
						{saving ? translations[language].calendar.editor.saving : translations[language].calendar.editor.save}
					</button>
				</div>
			</div>
		</div>
	);
};

export default AttributesModal;
